import { useMemo } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/library/card";
import { Info } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import PreferenceInlineItem from "./PreferenceInlineItem";
import { getChildModuleScopes } from "./getChildModuleScopes";
import { useInlineFieldEdit } from "../InlineEdit/useInlineFieldEdit";
import { useLeadForm } from "@/features/leads/contexts/FormLeadContext";
import { useChildForm } from "@/features/leads/contexts/ChildProvider";
import { useActiveDataMutation } from "@/features/leads/hook/useActiveDataMutation";
import { lead_spesific_key, child_spesific_key } from "@/config/endpoints";
import useSpesificCustoms from "@/features/customField/api/useSpesificCustoms";
import useResolveCustomDefaults, {
  type ResolvedField,
} from "@/features/customField/api/useResolveCustomDefaults";
import { usePermissions } from "@/rbac/usePermissions";
import { DEAL_EDIT_DENIED_MESSAGE } from "@/rbac/useCanEditDeal";
import { filterCustomInputFieldsByViewPermission } from "@/rbac/customFieldValueAccess";
import { useViewLeadFieldVisibility } from "@/features/leads/hook/useViewLeadFieldVisibility";
import { isAutoCreatedField } from "@/features/customField/utils/cfOrigin";
import { useLeadViewFieldOrder } from "@/features/leads/hook/useLeadViewFieldOrder";

/** Order keys are the custom field's id — the same key the edit mode's grips save. */
const cfKey = (f: any) => String(f?.id);

const LEAD_PREF_HINT =
  "Fields that apply to the whole lead — shared across every deal under this lead.";
const CHILD_PREF_HINT =
  "Fields specific to this deal only — they don't apply to other deals on this lead.";
const CAMPAIGN_PREF_HINT =
  "Answers this lead gave on the campaign form it came from. Only questions asked by this lead's campaign appear here. You can edit them, but the next sync from Meta may overwrite an empty one.";

function SectionHeading({ title, hint }: { title: string; hint: string }) {
  return (
    <div className="flex items-center gap-1.5">
      <CardTitle className="text-[12px] font-medium text-[var(--color-black-6)] tracking-[-0.24px]">
        {title}
      </CardTitle>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={`About ${title}`}
            className="text-[var(--color-black-5)] hover:text-[var(--color-black-7)] transition-colors"
          >
            <Info className="h-3.5 w-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-[260px] text-xs leading-snug">
          {hint}
        </TooltipContent>
      </Tooltip>
    </div>
  );
}

export default function PreferenceViewMode() {
  const { watch } = useLeadForm();
  const { customInputScopes: leadScopes } = watch();

  const { isChildActive, lead, child } = useActiveDataMutation();
  const childContext = useChildForm();
  const childFormData = childContext.watch();
  const childScopes = childFormData?.customInputScopes ?? [];

  const { spesificFields } = useSpesificCustoms({
    queryKey: lead_spesific_key,
    modelName: "leads",
    stageId: lead?.stage?.id,
    campaignId: lead?.campaign?.id,
  });

  const { spesificFields: spesificsChild } = useSpesificCustoms({
    queryKey: child_spesific_key,
    modelName: "child",
    stageId: child?.stage?.id,
    isActive: isChildActive,
  });

  const { canGlobal, getRawGlobalModule } = usePermissions();
  // Pass the raw reader too: without it the per-field gate cannot tell a
  // field the role simply never configured from one it explicitly denied.
  const cfPerms = { canGlobal, getRawGlobalModule };
  const { isFieldVisible, isCustomFieldVisible } = useViewLeadFieldVisibility();
  // Tenant-wide field order per section (arranged by dragging in edit mode).
  const leadPrefOrder = useLeadViewFieldOrder("leadPref");
  const dealPrefOrder = useLeadViewFieldOrder("dealPref");

  // RBAC filter first, then the admin's Manage → View Lead Display ticks.
  const allLeadFields = filterCustomInputFieldsByViewPermission(
    spesificFields,
    cfPerms
  ).filter((f: any) => isCustomFieldVisible(f.id));

  // Campaign answers live in their own read-only section: they belong to the
  // campaign form, not to the tenant's own field set. The server only returns
  // the ones this lead's campaign actually asks.
  const visibleLeadFields = allLeadFields.filter(
    (f: any) => !isAutoCreatedField(f),
  );
  const campaignFields = allLeadFields.filter((f: any) =>
    isAutoCreatedField(f),
  );
  const visibleChildFields = (
    isChildActive
      ? filterCustomInputFieldsByViewPermission(spesificsChild, cfPerms)
      : []
  ).filter((f: any) => isCustomFieldVisible(f.id));

  const {
    canInlineEdit,
    canInlineEditDeal,
    saveLeadCustomField,
    saveChildCustomField,
  } = useInlineFieldEdit();

  // Same option narrowing the deal's edit panel applies: a child CF only
  // offers what its attached niches (course, score, …) support. Only editors
  // need it, and empty scopes keep the resolver query disabled, so a
  // read-only viewer opening the drawer pays nothing.
  const childModuleScopes = useMemo(
    () =>
      isChildActive && canInlineEditDeal ? getChildModuleScopes(child) : [],
    [isChildActive, canInlineEditDeal, child],
  );
  const { resolvedFields: childResolvedFields } = useResolveCustomDefaults(
    childModuleScopes,
    "child",
  );
  const childResolvedMap = useMemo(
    () =>
      new Map<number, ResolvedField>(
        childResolvedFields.map((rf) => [rf.customInputId, rf]),
      ),
    [childResolvedFields],
  );

  // Section-level hide (Settings → Manage → View Lead Display). Edit mode
  // renders PreferenceEditMode instead, which is exempt.
  if (!isFieldVisible("preference")) {
    return null;
  }

  if (
    !visibleLeadFields.length &&
    !visibleChildFields.length &&
    !campaignFields.length
  ) {
    return null;
  }

  return (
    <Card className="border-[#e6e8e7] rounded-[12px] gap-0 py-2.5 shadow-none">
      <CardHeader className="px-3 pb-0">
        <CardTitle className="text-[14px] font-medium text-black tracking-[-0.28px]">
          Preference
        </CardTitle>
      </CardHeader>

      {visibleLeadFields.length > 0 && (
        <>
          <CardHeader className="px-3 pb-0 pt-1.5">
            <SectionHeading title="Lead Preference" hint={LEAD_PREF_HINT} />
          </CardHeader>
          <CardContent className="px-3 pt-0">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-y-3 gap-x-4 lg:gap-x-6 pl-1">
              {leadPrefOrder.sortByOrder(visibleLeadFields, cfKey).map((field: any, index: number) => (
                <PreferenceInlineItem
                  key={field.id || index}
                  field={field}
                  scopes={leadScopes}
                  canEdit={canInlineEdit}
                  onSave={saveLeadCustomField}
                />
              ))}
            </div>
          </CardContent>
        </>
      )}

      {isChildActive &&
        visibleChildFields.length > 0 &&
        visibleLeadFields.length > 0 && (
          <div className="mx-3 my-1 h-px bg-[#e6e8e7]" />
        )}

      {isChildActive && visibleChildFields.length > 0 && (
        <>
          <CardHeader className="px-3 pb-0 pt-1.5">
            <SectionHeading
              title="Child/Deal Preference"
              hint={
                canInlineEdit && !canInlineEditDeal
                  ? `${CHILD_PREF_HINT} ${DEAL_EDIT_DENIED_MESSAGE}.`
                  : CHILD_PREF_HINT
              }
            />
          </CardHeader>
          <CardContent className="px-3 pt-0">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-y-3 gap-x-4 lg:gap-x-6 pl-1">
              {dealPrefOrder.sortByOrder(visibleChildFields, cfKey).map((field: any, index: number) => (
                <PreferenceInlineItem
                  key={field.id || index}
                  field={field}
                  scopes={childScopes}
                  resolvedOptions={
                    childResolvedMap.get(field.id)?.resolvedOptions
                  }
                  canEdit={canInlineEditDeal}
                  onSave={saveChildCustomField}
                />
              ))}
            </div>
          </CardContent>
        </>
      )}

      {campaignFields.length > 0 &&
        (visibleLeadFields.length > 0 ||
          (isChildActive && visibleChildFields.length > 0)) && (
          <div className="mx-3 my-1 h-px bg-[#e6e8e7]" />
        )}

      {campaignFields.length > 0 && (
        <>
          <CardHeader className="px-3 pb-0 pt-1.5">
            <SectionHeading
              title="Campaign Preference"
              hint={CAMPAIGN_PREF_HINT}
            />
          </CardHeader>
          <CardContent className="px-3 pt-0">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-y-3 gap-x-4 lg:gap-x-6 pl-1">
              {leadPrefOrder.sortByOrder(campaignFields, cfKey).map((field: any, index: number) => (
                <PreferenceInlineItem
                  key={field.id || index}
                  field={field}
                  scopes={leadScopes}
                  canEdit={canInlineEdit}
                  onSave={saveLeadCustomField}
                />
              ))}
            </div>
          </CardContent>
        </>
      )}
    </Card>
  );
}
